import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import { Plus, X, Save, LayoutGrid, ListPlus, Coins, Timer, Map, Edit3, Loader2 } from 'lucide-react';
import { baseurl } from '../../baseurl';
import Swal from 'sweetalert2';

const AddReportTemplate = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    
    // Check if we are in Edit Mode
    const editId = queryParams.get('id');
    const action = queryParams.get('action');
    const isEditMode = editId && action === 'edit';

    // Basic Info States
    const [templateName, setTemplateName] = useState('');
    const [description, setDescription] = useState('');
    
    // Feature States
    const [featureInput, setFeatureInput] = useState('');
    const [features, setFeatures] = useState([]);
    
    // Pricing & Timing States
    const [creditsPerUnit, setCreditsPerUnit] = useState(1); 
    const [acreageBlock, setAcreageBlock] = useState(1);
    const [timeValue, setTimeValue] = useState(30);
    const [timeUnit, setTimeUnit] = useState('mins');
    
    // Loading States
    const [isFetching, setIsFetching] = useState(false); // For initial data load
    const [isSubmitting, setIsSubmitting] = useState(false); // For Save/Update button
    const adminToken = localStorage.getItem('adminToken');

    // --- FETCH DATA FOR EDIT MODE ---
    useEffect(() => {
        if (isEditMode) {
            fetchTemplateData();
        }
    }, [isEditMode, editId]);

    const fetchTemplateData = async () => {
        setIsFetching(true);
        try {
            const response = await axios.get(`${baseurl}getServicePackageById/${editId}`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            if (response.data.success) {
                const item = response.data.data;
                // Mapping API response to state
                setTemplateName(item.name || '');
                setDescription(item.description || '');
                setFeatures(item.features || []);
                setCreditsPerUnit(item.credit_rate || 0);
                setAcreageBlock(item.base_unit_acre || 1);
                setTimeValue(item.est_time_value || 0);
                setTimeUnit(item.est_time_unit || 'mins');
            }
        } catch (err) {
            console.error("Fetch Error:", err);
            Swal.fire('Error', 'Could not load template data', 'error');
        } finally {
            // Artificial delay for smooth transition if desired, or immediate
            setTimeout(() => setIsFetching(false), 500);
        }
    };

    const addFeature = (e) => {
        if (e.key === 'Enter' || e.type === 'click') {
            e.preventDefault();
            const value = featureInput.trim();
            if (value && !features.includes(value)) {
                setFeatures([...features, value]);
                setFeatureInput('');
            }
        }
    };

    const removeFeature = (indexToRemove) => {
        setFeatures(features.filter((_, index) => index !== indexToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (features.length === 0) {
            Swal.fire('Error', 'Please add at least one measurable feature.', 'error');
            return;
        }

        setIsSubmitting(true);
        
        const payload = {
            name: templateName,
            description: description,
            features: features,
            creditRate: Number(creditsPerUnit),
            baseUnitAcre: Number(acreageBlock),
            estTimeValue: Number(timeValue),
            estTimeUnit: timeUnit
        };

        try {
            const url = isEditMode 
                ? `${baseurl}updateServicePackageById/${editId}` 
                : `${baseurl}createServicePackage`;
            
            const response = await axios.post(url, payload, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });

            if (response.data.success) {
                Swal.fire({
                    icon: 'success',
                    title: isEditMode ? 'Template Updated' : 'Template Created',
                    text: response.data.message || 'Service package saved successfully!',
                    timer: 2000
                });
                if (!isEditMode) {
                    // Reset form
                    setTemplateName('');
                    setDescription('');
                    setFeatures([]);
                } else {
                    navigate('/all-reports'); // Redirect after update
                }
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'Failed to save template.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="app-container">
            <Topbar />
            <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex' }}>
                {/* <SideMenu /> */}
                <div className="profile-content-container p-4" style={{ flex: 1, overflow: 'auto' }}>
                    
                    <div className="mb-4 d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="fw-bold" style={{ color: '#1B2559' }}>
                                {isEditMode ? 'Edit Template' : 'Manage Templates'}
                            </h2>
                            <p className="text-muted">
                                {isEditMode ? `Updating ID: ${editId}` : 'Define credit consumption and processing speed per acre.'}
                            </p>
                        </div>
                        {isEditMode && (
                             <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={() => navigate('/all-report')}>
                                Cancel Edit
                             </button>
                        )}
                    </div>

                    {isFetching ? (
                        <div className="d-flex flex-column align-items-center justify-content-center mt-5 py-5 bg-white shadow-sm rounded-4">
                            <Loader2 className="text-primary animate-spin mb-3" size={48} />
                            <h5 className="text-muted fw-bold">Loading Template Data...</h5>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-lg-7">
                                <div className="premium-card bg-white p-4 shadow-sm" style={{ borderRadius: '20px' }}>
                                    <div className="section-header d-flex align-items-center gap-3 mb-4">
                                        <div className="icon-wrap p-2 rounded-3" style={{ background: '#F4F7FE', color: '#4318FF' }}>
                                            {isEditMode ? <Edit3 size={24} /> : <ListPlus size={24} />}
                                        </div>
                                        <h5 className="card-title mb-0 fw-bold">
                                            {isEditMode ? 'Modify Configuration' : 'Template Configurator'}
                                        </h5>
                                    </div>

                                    <form onSubmit={handleSubmit}>
                                        <div className="mb-3">
                                            <label className="fw-bold mb-1 small">Template Name</label>
                                            <input type="text" className="form-control" placeholder="e.g., AI Orchard Scan" value={templateName} onChange={(e) => setTemplateName(e.target.value)} required style={{ borderRadius: '10px' }} />
                                        </div>

                                        <div className="mb-4">
                                            <label className="fw-bold mb-1 small">Description</label>
                                            <textarea className="form-control" rows="2" value={description} onChange={(e) => setDescription(e.target.value)} style={{ borderRadius: '10px' }}></textarea>
                                        </div>

                                        <div className="p-4 mb-4 rounded-4" style={{ background: '#F8F9FF', border: '1px dashed #4318FF' }}>
                                            <h6 className="text-primary fw-bold mb-3 d-flex align-items-center gap-2">
                                                <Coins size={16} /> Pricing & Speed
                                            </h6>
                                            <div className="row g-3">
                                                <div className="col-md-4">
                                                    <label className="x-small fw-bold text-muted mb-1 uppercase">Credits</label>
                                                    <input type="number" className="form-control" value={creditsPerUnit} onChange={(e) => setCreditsPerUnit(e.target.value)} min="0.1" step="0.1" />
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="x-small fw-bold text-muted mb-1 uppercase">Per Acreage</label>
                                                    <div className="input-group">
                                                        <input type="number" className="form-control" value={acreageBlock} onChange={(e) => setAcreageBlock(e.target.value)} min="1" />
                                                        <span className="input-group-text bg-white small">Ac</span>
                                                    </div>
                                                </div>
                                                <div className="col-md-4">
                                                    <label className="x-small fw-bold text-muted mb-1 uppercase">Process Time</label>
                                                    <div className="input-group">
                                                        <input type="number" className="form-control" value={timeValue} onChange={(e) => setTimeValue(e.target.value)} min="1" />
                                                        <select className="form-select small" value={timeUnit} onChange={(e) => setTimeUnit(e.target.value)}>
                                                            <option value="mins">Mins</option>
                                                            <option value="secs">Secs</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mb-3">
                                            <label className="fw-bold mb-1 small text-muted uppercase">Add Measurable Features</label>
                                            <div className="d-flex gap-2">
                                                <input type="text" className="form-control" placeholder="Tree Count, Health Index..." value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={addFeature} />
                                                <button type="button" className="btn btn-primary px-3 rounded-3" onClick={addFeature}><Plus /></button>
                                            </div>
                                        </div>

                                        <div className="features-preview-area p-3 rounded-4 mb-4" style={{ background: '#F4F7FE', minHeight: '80px', border: '1px solid #E0E5F2' }}>
                                            <div className="d-flex flex-wrap gap-2">
                                                {features.map((f, i) => (
                                                    <span key={i} className="badge bg-white text-primary border px-3 py-2 rounded-pill shadow-sm d-flex align-items-center gap-2">
                                                        {f} <X size={14} className="text-danger cursor-pointer" onClick={() => removeFeature(i)} />
                                                    </span>
                                                ))}
                                                {features.length === 0 && <small className="text-muted italic">No features added yet...</small>}
                                            </div>
                                        </div>

                                        <button type="submit" className="btn-save-changes w-100 py-3 shadow-lg" disabled={isSubmitting}>
                                            {isSubmitting ? (
                                                <><Loader2 className="animate-spin me-2" size={18} /> Saving...</>
                                            ) : (
                                                <><Save size={18} className="me-2" /> {isEditMode ? 'Update Template' : 'Deploy Template'}</>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </div>

                            <div className="col-lg-5">
                                <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '24px', background: 'linear-gradient(135deg, #4318FF 0%, #707EAE 100%)', color: 'white', position: 'sticky', top: '20px' }}>
                                    <LayoutGrid size={32} className="mb-3 opacity-50" />
                                    <h5 className="fw-bold">Marketplace Preview</h5>
                                    <div className="bg-white text-dark rounded-4 p-4 mt-2 shadow-lg">
                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                            <h6 className="fw-bold text-primary mb-0" style={{fontSize: '18px'}}>{templateName || 'Report Name'}</h6>
                                            <span className="badge rounded-pill" style={{background: '#E0E7FF', color: '#4318FF', fontSize: '11px'}}>
                                                {creditsPerUnit} Cr / {acreageBlock} Ac
                                            </span>
                                        </div>
                                        <p className="small text-muted mb-4">{description || 'No description provided yet...'}</p>
                                        <div className="row g-2 mb-4">
                                            <div className="col-6">
                                                <div className="p-2 rounded-3 text-start" style={{ background: '#F4F7FE' }}>
                                                    <div className="text-muted" style={{fontSize: '10px'}}><Timer size={10} /> SPEED</div>
                                                    <div className="small fw-bold text-dark">{timeValue}{timeUnit.charAt(0)} / Acre</div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="p-2 rounded-3 text-start" style={{ background: '#F4F7FE' }}>
                                                    <div className="text-muted" style={{fontSize: '10px'}}><Map size={10} /> UNIT</div>
                                                    <div className="small fw-bold text-dark">{acreageBlock} Ac</div>
                                                </div>
                                            </div>
                                        </div>
                                        <label className="x-small fw-bold text-muted mb-2 uppercase">Analysis Includes:</label>
                                        <div className="d-flex flex-wrap gap-1">
                                            {features.length > 0 ? features.slice(0, 3).map((f, i) => (
                                                <span key={i} className="badge bg-light text-dark py-1 px-2" style={{fontSize: '10px'}}>{f}</span>
                                            )) : <span className="x-small text-muted italic">No features defined</span>}
                                            {features.length > 3 && <span className="x-small text-muted">+{features.length - 3} more</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <style>{`
                .animate-spin { animation: spin 1s linear infinite; }
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
                .btn-save-changes {
                    background: #4318FF;
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-weight: 600;
                    transition: all 0.3s;
                }
                .btn-save-changes:hover { background: #3311CC; transform: translateY(-2px); }
                .btn-save-changes:disabled { background: #A3AED0; cursor: not-allowed; }
                .x-small { font-size: 10px; }
                .uppercase { text-transform: uppercase; letter-spacing: 0.5px; }
            `}</style>
        </div>
    );
};

export default AddReportTemplate;